<template>
  <div class="page-top-space"></div>
  <div class="dossiers" :class="{'detail-open': mobileDetail}">
    <CustomLoader ref="loader"/>

    <OrderList class="list" :orders="decoratedOrders" :selectedId="selectedId" :selectCallback="selectOrder" />

    <div v-if="selectedOrder" class="detail">
      <OrderSummary :order="selectedOrder" :finance="selectedOrder.finance"
                    :editCallback="() => $refs.editOrderModal.openModal(selectedOrder)" :deleteCallback="deleteOrder" :backCallback="closeDetail" />
      <div class="panels">
        <OrderContracts :order="selectedOrder" :refreshCallback="loadData" />
        <OrderPurchases v-if="hasPurchase" :order="selectedOrder" :items="selectedOrder.items" :finance="selectedOrder.finance"
                        :purchases="purchases" :refreshCallback="loadData" />
      </div>
    </div>
    <div v-else-if="!isLoading" class="detail placeholder">
      <span class="fas fa-folder-open"></span>
      <p>{{ orders.length === 0 ? "Aucun dossier pour l'instant." : 'Sélectionnez un dossier pour voir ses contrats et ses achats.' }}</p>
    </div>
  </div>

  <EditOrderModal ref="editOrderModal" :callback="loadData" />
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core';
import {faFolderOpen} from "@fortawesome/free-solid-svg-icons";
import router from "@/router";
import CustomLoader from "@/components/CustomLoader.vue";
import EditOrderModal from "@/components/modals/EditOrderModal.vue";
import OrderList from "@/components/production/OrderList.vue";
import OrderSummary from "@/components/production/OrderSummary.vue";
import OrderContracts from "@/components/production/OrderContracts.vue";
import OrderPurchases from "@/components/production/OrderPurchases.vue";
import {ProductionController} from "@/controllers/production/ProductionController";
import {PurchaseController} from "@/purchase/controllers/PurchaseController";
import {loadVatRegimes} from "@/purchase/services/VatRegimeService";
import {ORDER_STAGE, getOrderStage, orderFinance, purchasesOfOrder, stageLabel} from "@/components/production/productionUtils";
library.add(faFolderOpen);

const normalize = text => String(text ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Dossiers : à gauche la liste, à droite la fiche du dossier sélectionné avec ses contrats et, si l'option achats
// est active, les achats qui lui sont rattachés (marge comprise). Le dossier sélectionné est dans l'URL (/dossiers/:id).
export default {
  name: 'ProductionView',
  components: {CustomLoader, EditOrderModal, OrderList, OrderSummary, OrderContracts, OrderPurchases},
  data() {
    return {
      orders: [],
      purchases: [],
      vatRegimes: [],
      hasPurchase: false,
      selectedId: undefined,
      // Petit écran : la liste et la fiche ne tiennent pas ensemble, on affiche l'une ou l'autre
      mobileDetail: false,
      isLoading: true,
    };
  },
  computed: {
    // Dossiers avec leur étape, leurs achats, leurs chiffres et le texte de recherche de la liste
    decoratedOrders() {
      return this.orders.map(order => {
        const items = this.hasPurchase ? purchasesOfOrder(this.purchases, order.id, this.vatRegimes) : [];
        return {
          ...order,
          stage: getOrderStage(order),
          items,
          finance: orderFinance(order, items, this.hasPurchase),
          searchText: normalize([
            order.number, order.title, ...(order.clientNames ?? []),
            ...order.businesses.flatMap(business => ['D' + business.number, business.title, business.invoiceNumber ? 'FA' + business.invoiceNumber : '']),
            ...items.map(item => item.purchase.supplierName + ' ' + (item.purchase.reference ?? '')),
            stageLabel(getOrderStage(order)),
          ].join(' ')),
        };
      });
    },
    selectedOrder() {
      return this.decoratedOrders.find(order => order.id === this.selectedId);
    },
  },
  watch: {
    // Navigation (retour arrière, lien d'un autre écran) : la sélection suit l'URL
    "$route.params.id"(id) {
      if (!id) {
        // Retour sur /dossiers (menu) : liste seule sur mobile, la sélection courante reste dans l'URL
        if (!this.$route.path.startsWith("/dossiers")) return;
        this.mobileDetail = false;
        this.syncUrl();
      } else if (id !== this.selectedId && this.orders.some(order => order.id === id)) {
        this.selectedId = id;
      }
    },
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      this.$refs.loader?.startLoading();

      this.hasPurchase = !!(localStorage.options && localStorage.options.includes("purchase"));
      const [orders, purchases, vatRegimes] = await Promise.all([
        ProductionController.getAll(""),
        this.hasPurchase ? PurchaseController.getTable("") : Promise.resolve([]),
        this.hasPurchase ? loadVatRegimes() : Promise.resolve([]),
      ]);
      this.vatRegimes = vatRegimes;

      this.orders = orders && !orders.errors ? [...orders].sort((a, b) => Number(b.number) - Number(a.number)) : [];
      // Si les achats sont indisponibles, la fiche reste utilisable sans eux
      if (this.hasPurchase && (!purchases || purchases.errors)) this.hasPurchase = false;
      this.purchases = this.hasPurchase ? purchases : [];

      this.restoreSelection();
      this.isLoading = false;
      this.$refs.loader?.stopLoading();
    },
    // Dossier de l'URL (/dossiers/:id, ou ?orderId=<numéro> venant d'un contrat), sinon celui qui était sélectionné, sinon le plus récent
    restoreSelection() {
      const fromQuery = this.$route.query.orderId ? this.orders.find(order => String(order.number) === String(this.$route.query.orderId)) : undefined;
      const wanted = [fromQuery?.id, this.$route.params.id, this.selectedId].find(id => id && this.orders.some(order => order.id === id));
      // Sans dossier demandé, on ouvre le plus récent des dossiers en cours (filtre par défaut de la liste)
      const defaultOrder = this.orders.find(order => getOrderStage(order) === ORDER_STAGE.IN_PROGRESS.value) ?? this.orders[0];
      this.selectedId = wanted ?? defaultOrder?.id;
      const routeOrderExists = !!this.$route.params.id && this.orders.some(order => order.id === this.$route.params.id);
      this.mobileDetail = this.mobileDetail || !!fromQuery || routeOrderExists;
      this.syncUrl();
    },
    syncUrl() {
      if (this.selectedId && (this.$route.params.id !== this.selectedId || Object.keys(this.$route.query).length > 0 || this.$route.hash)) {
        router.replace({path: `/dossiers/${this.selectedId}`});
      }
    },
    selectOrder(id) {
      this.selectedId = id;
      this.mobileDetail = true;
      this.syncUrl();
    },
    closeDetail() {
      this.mobileDetail = false;
    },
    async deleteOrder() {
      const order = this.selectedOrder;
      if (!order || order.businesses.length > 0) return;
      this.$refs.loader?.startLoading();
      const resp = await ProductionController.delete(order.id);
      this.$refs.loader?.stopLoading();
      if (!resp.errors) {
        this.selectedId = undefined;
        this.mobileDetail = false;
        await router.replace({path: "/dossiers"});
        await this.loadData();
      }
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

h1
  color THEME_COLOR_PRIMARY

  span
    color THEME_COLOR_PRIMARY_FADDED

.dossiers
  position relative
  display grid
  grid-template-columns 340px minmax(0, 1fr)
  align-items start
  gap 16px
  margin-top 10px

.list
  position sticky
  top 12px
  max-height calc(100vh - 150px)

.detail
  display flex
  flex-direction column
  gap 16px
  min-width 0

  &.placeholder
    align-items center
    justify-content center
    min-height 280px
    border 1px dashed #d6dcea
    border-radius 14px
    color #8a93a8

    span
      font-size 34px
      color #c3cbe0

.panels
  display grid
  grid-template-columns repeat(auto-fit, minmax(420px, 1fr))
  gap 16px
  align-items start

// Petit écran : la liste puis, une fois un dossier choisi, seulement sa fiche
@media (max-width: 1000px)
  .dossiers
    grid-template-columns 1fr

  .list
    position static
    max-height none

  .dossiers.detail-open .list
    display none

  .dossiers:not(.detail-open) .detail
    display none

@media (max-width: 576px)
  .panels
    grid-template-columns 1fr
</style>

<style lang="stylus">
main
  align-content start
</style>
